<template>
  <div class="station-form-category-container flex flex-col gap-y-2 pb-6">
    <span class="station-form-category-title font-bold">Observações</span>

    <div class="flex flex-col gap-3">
      <div v-if="isLoadingList" class="text-gray-500 text-sm text-left">
        Carregando observações...
      </div>
      <div
        v-if="localObservations.length === 0 && !isLoadingList"
        class="text-gray-500 text-sm text-left"
      >
        Nenhuma observação registrada.
      </div>
      <div
        v-for="(obs, index) in localObservations"
        :key="obs.id || index"
        class="bg-gray-100 p-3 rounded-lg flex justify-between items-start"
      >
        <div class="text-start">
          <p class="font-bold text-sm">
            {{ obs.author }}
            <span class="text-xs font-normal text-gray-500"> | {{ obs.timestamp }} </span>
          </p>
          <p class="text-sm mt-1 text-left">{{ obs.text }}</p>
        </div>
        <v-tooltip text="Remover">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              @click="handleDelete(obs, index)"
              color="error"
              variant="text"
              :icon="mdiTrashCanOutline"
              density="compact"
              size="small"
              :disabled="!canDeleteObservations && getUserId != obs.authorId"
            />
          </template>
        </v-tooltip>
      </div>
    </div>

    <div class="flex border rounded px-3 py-3 gap-4 items-start mt-2">
      <input
        v-model="draftObservation"
        type="text"
        placeholder="Digite uma observação..."
        class="flex-grow border-none outline-none text-sm placeholder-gray-500"
        @keyup.enter="handleSaveDraft"
      />

      <FilledButton
        v-if="canCreateObservation"
        type="button"
        color="primary30"
        class="text-white text-xs font-medium"
        @click="handleSaveDraft"
        width="130"
        height="32"
        :loading="isLoading"
        :disabled="!draftObservation"
      >
        Nova observação
      </FilledButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import NetworkService from '@/Network/index'
import { DoctorStationObservationListResponse } from '@/Network/Types/Responses/DsObservationResponse'
import { mdiTrashCanOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { subHours, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

interface Observation {
  id?: number
  authorId?: number
  text: string
  author?: string
  timestamp: string
}

const props = defineProps<{
  doctorStationId?: string | number | undefined
  scheduleId?: string | number | undefined
  timezone?: string
}>()

const isLoading = ref(false)
const isLoadingList = ref(false)
const toast = useToast()
const { notifyError } = useErrorToast()
const draftObservation = ref('')
const listObservations = ref<DoctorStationObservationListResponse>({ data: [], count: 0 })
const { getUserId } = useAuthStore()
const permissionsStore = usePermissionsStore()

const canDeleteObservations = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationObservationsDeleting,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: Number(props.scheduleId)
    }
  })
})

const canCreateObservation = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationObservationsCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

function parseOffsetHours(offset: string | undefined): number {
  if (!offset) return 3
  try {
    const hours = parseInt(offset.split(':')[0], 10)
    return Math.abs(hours)
  } catch {
    return 3
  }
}

const localObservations = computed<Observation[]>(() =>
  listObservations.value.data.map((obs) => {
    let formattedTimestamp = ''

    if (obs.createdAt) {
      try {
        const utcDate = new Date(obs.createdAt)
        const hoursToSubtract = parseOffsetHours(props.timezone)
        const localDate = subHours(utcDate, hoursToSubtract)
        formattedTimestamp = format(localDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
      } catch (e) {
        console.error('Erro ao formatar data:', e)
        formattedTimestamp = 'Data inválida'
      }
    }

    return {
      id: obs.id,
      author: obs.author?.name ?? 'Usuário',
      authorId: obs.author?.id ?? null,
      text: obs.text,
      timestamp: formattedTimestamp
    }
  })
)

const fetchObservations = async () => {
  if (!props.doctorStationId) return
  try {
    isLoadingList.value = true
    const fetchedObs = await NetworkService.observation.list(Number(props.doctorStationId))
    listObservations.value = fetchedObs
  } catch (error) {
    // Antes: swallow com console.error — a falha de load parecia "sem observações".
    console.error('Erro ao buscar observações:', error)
    notifyError(error, 'Não foi possível carregar as observações')
  } finally {
    isLoadingList.value = false
  }
}

const handleSaveDraft = async () => {
  const text = draftObservation.value.trim()
  if (!text) return

  try {
    isLoading.value = true
    if (props.doctorStationId) {
      const saved = await NetworkService.observation.create(Number(props.doctorStationId), {
        text,
        scheduleId: Number(props.scheduleId)
      })
      listObservations.value.data.unshift(saved)
      listObservations.value.count++
    }
    draftObservation.value = ''
    toast.success('Observação criada com sucesso!')
  } catch (err) {
    // Antes: "Erro ao criar observação!" — a mensagem específica do
    // ServiceError chega intacta ao usuário.
    notifyError(err, 'Não foi possível criar a observação')
    console.error('Erro ao salvar observação:', err)
  } finally {
    isLoading.value = false
  }
}

const handleDelete = async (observation: Observation, index: number) => {
  if (props.doctorStationId && observation.id) {
    try {
      await NetworkService.observation.delete(
        Number(observation.id),
        Number(props.scheduleId),
        Number(observation?.authorId)
      )
      listObservations.value.data.splice(index, 1)
      listObservations.value.count--
      toast.success('Observação removida com sucesso.')
    } catch (error) {
      console.error('Erro ao deletar observação:', error)
      // Antes: "Erro ao deletar observação." estático.
      notifyError(error, 'Não foi possível remover a observação')
      return
    }
  }
}

onMounted(async () => {
  await fetchObservations()
})
</script>
